import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

async function collect(): Promise<RawData> {
  let acceptLanguage = '';
  try {
    const response = await fetch('/network.json', { cache: 'no-store' });
    const body: unknown = await response.json();
    if (isRecord(body) && isRecord(body.headers)) {
      acceptLanguage = String(body.headers['accept-language'] ?? '');
    }
  } catch {
    acceptLanguage = '';
  }
  const utcDate = new Date('2025-01-15T12:34:56Z');
  return {
    language: navigator.language,
    languages: [...navigator.languages],
    acceptLanguage,
    numberSamples: [1234.5, -0.25, 1_000_000].map((value) =>
      new Intl.NumberFormat('ru-RU').format(value),
    ),
    dateSamplesUtc: [
      new Intl.DateTimeFormat('ru-RU', {
        timeZone: 'UTC',
        dateStyle: 'full',
      }).format(utcDate),
    ],
    collatorOrder: ['е', 'ё', 'ж'].sort(new Intl.Collator('ru-RU').compare),
    pluralSamples: [1, 2, 5, 21].map((value) =>
      new Intl.PluralRules('ru-RU').select(value),
    ),
    relativeTimeSamples: [-1, 0, 1].map((value) =>
      new Intl.RelativeTimeFormat('ru-RU').format(value, 'day'),
    ),
    segmenterSamples: [
      ...new Intl.Segmenter('ru-RU', { granularity: 'word' }).segment(
        'Привет, мир!',
      ),
    ].map((part) => `${part.segment}:${part.isWordLike ? '1' : '0'}`),
  };
}

function validateLocale(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  if (rawData.language !== 'ru-RU') {
    reasons.push('Primary language must be ru-RU');
  }
  if (!Array.isArray(rawData.languages) || rawData.languages[0] !== 'ru-RU') {
    reasons.push('navigator.languages must begin with ru-RU');
  }
  if (
    String(rawData.acceptLanguage) &&
    !String(rawData.acceptLanguage).toLowerCase().startsWith('ru')
  ) {
    reasons.push('Accept-Language must begin with Russian');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return String(rawData.language);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateLocale(rawData);
}

export const localeIntlTest: TestDefinition = {
  id: 'locale',
  title: 'Locale',
  category: 'shared',
  format,
  run,
  validate,
};
